import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import UserBadge from "@/components/UserBadge";
import { X } from "@phosphor-icons/react";

const REACTION_META = {
  like: { emoji: "👍", label: "Like" },
  love: { emoji: "❤️", label: "Love" },
  haha: { emoji: "😆", label: "Haha" },
  wow: { emoji: "😮", label: "Wow" },
  sad: { emoji: "😢", label: "Sad" },
  angry: { emoji: "😡", label: "Angry" },
};

function resolveImg(u) {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${API_BASE.replace(/\/api$/, "")}${u}`;
  return u;
}

export default function LikesModal({ postId, initialTab = "all", onClose }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/feed/${postId}/reactions`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [postId]);

  const rows = tab === "all" ? (data?.users || []) : (data?.by_reaction?.[tab] || []);
  const availableTabs = Object.keys(data?.by_reaction || {});

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="likes-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-md max-h-[85vh] overflow-hidden flex flex-col"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-800">
          <h3
            className="font-black text-slate-900 dark:text-white text-lg"
            style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}
          >
            Reactions {data?.total ? `· ${data.total}` : ""}
          </h3>
          <button data-testid="likes-modal-close" onClick={onClose} className="h-8 w-8 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 inline-flex items-center justify-center">
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Tabs */}
        {availableTabs.length > 0 && (
          <div className="flex gap-1 px-3 py-2 border-b border-slate-100 dark:border-slate-800 overflow-x-auto">
            <button
              onClick={() => setTab("all")}
              data-testid="likes-tab-all"
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-bold transition-colors duration-200 ${tab === "all" ? "bg-blue-700 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700"}`}
            >
              All {data?.total || 0}
            </button>
            {availableTabs.map((k) => {
              const meta = REACTION_META[k] || { emoji: "•", label: k };
              const count = data?.by_reaction?.[k]?.length || 0;
              return (
                <button
                  key={k}
                  data-testid={`likes-tab-${k}`}
                  onClick={() => setTab(k)}
                  className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1 transition-colors duration-200 ${tab === k ? "bg-blue-700 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700"}`}
                >
                  <span className="text-sm leading-none">{meta.emoji}</span>
                  {count}
                </button>
              );
            })}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {loading && <div className="text-sm text-slate-500 text-center py-8">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="text-sm text-slate-500 text-center py-8">No reactions yet.</div>
          )}
          {rows.map((r) => {
            const src = resolveImg(r.avatar_url);
            const meta = REACTION_META[r.reaction] || {};
            return (
              <Link
                key={`${r.id}-${r.reaction}`}
                to={`/u/${r.id}`}
                data-testid={`liker-${r.username}`}
                onClick={onClose}
                className="flex items-center gap-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 px-2 py-2 transition-colors duration-200"
              >
                <div className="relative shrink-0">
                  {src ? (
                    <img src={src} alt={r.username} className="h-11 w-11 rounded-full object-cover" />
                  ) : (
                    <div className="h-11 w-11 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center font-black">
                      {(r.username || "?").slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <span className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-white dark:bg-slate-900 ring-1 ring-slate-200 dark:ring-slate-700 flex items-center justify-center text-xs" title={meta.label}>
                    {meta.emoji || "👍"}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-slate-900 dark:text-white inline-flex items-center gap-1">
                    {r.username}
                    <UserBadge user={r} size={12} />
                  </div>
                  {r.full_name && <div className="text-xs text-slate-500 truncate">{r.full_name}</div>}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
